import {
  ChangeDetectionStrategy,
  booleanAttribute,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';

/**
 * The page's one search field (R2), first in the band (R1). Searches by itself 1 s after the last
 * keystroke, or at once on Enter (R14). Below {@link minLength} characters it shows a hint and
 * sends nothing; emptying the field sends `''` at once.
 */
@Component({
  selector: 'abook-filter-search',
  imports: [MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'abook-filter-search',
    '[class.is-changed]': '!!value()',
    '[class.abook-filter-search--compact]': 'compact()',
  },
  template: `
    <label class="search" [class.search--filled]="!!draft()">
      <mat-icon aria-hidden="true">{{ icon() }}</mat-icon>
      <input
        type="search"
        [placeholder]="placeholder()"
        [attr.aria-label]="ariaLabel() || placeholder()"
        [attr.aria-describedby]="hint() ? hintId : null"
        [value]="draft()"
        (input)="onInput($any($event.target).value)"
        (keydown.enter)="flush()"
        (keydown.escape)="clear()"
      />
      @if (draft()) {
        <button type="button" class="search__clear" aria-label="Ryd søgning" (click)="clear()">
          <mat-icon aria-hidden="true">close</mat-icon>
        </button>
      }
    </label>
    @if (hint()) {
      <span class="search__hint" [id]="hintId" role="status">{{ hint() }}</span>
    }
  `,
  styleUrl: './abook-filter-search.component.scss',
})
export class AbookFilterSearchComponent {
  private static nextId = 0;

  readonly value = input<string>('');
  readonly placeholder = input('Søg');
  /** `search` in the band; `filter_list` for the table bar's result filter. */
  readonly icon = input('search');
  /** Spelled out for screen readers when the placeholder is short: "Søg i navn, mail, telefon og ordreID". */
  readonly ariaLabel = input('');
  /** Fewer characters than this (but more than none) send nothing and show "Mindst n tegn". */
  readonly minLength = input(0);
  readonly debounceMs = input(1000);
  readonly compact = input(false, { transform: booleanAttribute });

  readonly valueChange = output<string>();

  protected readonly hintId = `abook-filter-search-hint-${AbookFilterSearchComponent.nextId++}`;
  protected readonly draft = signal('');
  protected readonly hint = computed(() => {
    const length = this.draft().trim().length;
    return length > 0 && length < this.minLength() ? `Mindst ${this.minLength()} tegn` : '';
  });

  private timer: ReturnType<typeof setTimeout> | undefined;

  constructor() {
    // A reset or a pasted link changes the value from outside; the field follows it.
    effect(() => {
      const value = this.value();
      // Only the value is tracked: typing must not run this and put the old value back.
      untracked(() => {
        if (value.trim() !== this.draft().trim()) this.draft.set(value);
      });
    });
    inject(DestroyRef).onDestroy(() => clearTimeout(this.timer));
  }

  protected onInput(text: string): void {
    this.draft.set(text);
    clearTimeout(this.timer);
    if (!text.trim()) this.flush();
    else this.timer = setTimeout(() => this.flush(), this.debounceMs());
  }

  protected flush(): void {
    clearTimeout(this.timer);
    const text = this.draft().trim();
    if (text.length > 0 && text.length < this.minLength()) return;
    if (text !== this.value().trim()) this.valueChange.emit(text);
  }

  protected clear(): void {
    this.draft.set('');
    this.flush();
  }
}
